import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { DEFAULT_MENU } from "@/data/menu";
import { LOGO_URL } from "@/data/homeContent";
import { ChevronDown, Menu, X } from "lucide-react";

const linkBase = "px-3 py-2 text-[15px] font-medium text-slate-700 hover:text-[#800020] transition-colors";

const CountryMega = ({ countries }) => (
  <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(90vw,1050px)]">
    <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-7 max-h-[75vh] overflow-y-auto">
      {countries.length === 0 ? (
        <p className="text-sm text-slate-500">Loading directory…</p>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-8">
          {countries.map((c) => (
            <div key={c.country} className="break-inside-avoid mb-6">
              <span className="block font-serif text-lg font-semibold text-[#800020] uppercase tracking-wide" data-testid={`megamenu-country-${c.country}`}>{c.country}</span>
              <ul className="mt-2 space-y-1.5">
                {c.schools.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/school/${s.slug}`} className="text-[13.5px] text-slate-600 hover:text-[#800020] transition-colors leading-snug" data-testid={`megamenu-school-${s.slug}`}>{s.school_name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

export const Header = () => {
  const [countries, setCountries] = useState([]);
  const [menu, setMenu] = useState(DEFAULT_MENU);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobile, setOpenMobile] = useState({});

  useEffect(() => {
    api.get("/countries").then((res) => setCountries(res.data)).catch(() => {});
    api.get("/menu").then((res) => { if (Array.isArray(res.data) && res.data.length) setMenu(res.data); }).catch(() => {});
  }, []);

  const closeMobile = () => { setMobileOpen(false); setOpenMobile({}); };
  const toggleMobile = (id) => setOpenMobile((o) => ({ ...o, [id]: !o[id] }));

  const visibleMenu = menu.filter((m) => m.visible !== false);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img src={LOGO_URL} alt="BSD Boarding School Directory" className="h-11 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center">
            {visibleMenu.map((item) => {
              if (item.type === "search") {
                return (
                  <div key={item.id} className="relative group" data-testid="nav-search-megamenu">
                    <button className={`${linkBase} inline-flex items-center gap-1`} data-testid="nav-search-megamenu-trigger">
                      {item.label} <ChevronDown className="h-4 w-4" />
                    </button>
                    <CountryMega countries={countries} />
                  </div>
                );
              }
              if (item.type === "dropdown") {
                const kids = (item.children || []).filter((ch) => ch.visible !== false);
                return (
                  <div key={item.id} className="relative group" data-testid={`nav-dropdown-${item.id}`}>
                    <button className={`${linkBase} inline-flex items-center gap-1`} data-testid={`nav-dropdown-trigger-${item.id}`}>
                      {item.label} <ChevronDown className="h-4 w-4" />
                    </button>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 absolute left-0 top-full pt-3 w-64">
                      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 py-2">
                        {kids.map((ch) => (
                          <Link key={ch.to + ch.label} to={ch.to} className="block px-5 py-2.5 text-sm text-slate-700 hover:bg-[#faf6f0] hover:text-[#800020] transition-colors">{ch.label}</Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link key={item.id} to={item.to || "/"} className={linkBase} data-testid={`nav-link-${item.id}`}>{item.label}</Link>
              );
            })}
          </nav>

          <button className="lg:hidden p-2 text-slate-700" onClick={() => setMobileOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white max-h-[82vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="px-5 py-3">
            {visibleMenu.map((item) => {
              if (item.type === "search") {
                return (
                  <div key={item.id} className="border-b border-slate-100">
                    <button onClick={() => toggleMobile(item.id)} className="w-full flex items-center justify-between py-3 text-slate-800 font-medium" data-testid="mobile-search-toggle">
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${openMobile[item.id] ? "rotate-180" : ""}`} />
                    </button>
                    {openMobile[item.id] && (
                      <div className="pb-3 pl-2 space-y-3 max-h-[45vh] overflow-y-auto" data-testid="mobile-search-panel">
                        {countries.map((c) => (
                          <div key={c.country}>
                            <p className="text-sm font-semibold text-[#800020] uppercase tracking-wide">{c.country}</p>
                            <ul className="mt-1.5 space-y-1 pl-2">
                              {c.schools.map((s) => (
                                <li key={s.slug}><Link to={`/school/${s.slug}`} onClick={closeMobile} className="text-[13.5px] text-slate-600">{s.school_name}</Link></li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (item.type === "dropdown") {
                const kids = (item.children || []).filter((ch) => ch.visible !== false);
                return (
                  <div key={item.id} className="border-b border-slate-100">
                    <button onClick={() => toggleMobile(item.id)} className="w-full flex items-center justify-between py-3 text-slate-800 font-medium" data-testid={`mobile-dropdown-toggle-${item.id}`}>
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${openMobile[item.id] ? "rotate-180" : ""}`} />
                    </button>
                    {openMobile[item.id] && (
                      <div className="pb-3 pl-3 space-y-2">
                        {kids.map((ch) => (
                          <Link key={ch.to + ch.label} to={ch.to} onClick={closeMobile} className="block text-sm text-slate-600">{ch.label}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link key={item.id} to={item.to || "/"} onClick={closeMobile} className="block py-3 text-slate-800 font-medium border-b border-slate-100">{item.label}</Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
};
